import { END_POINTS } from '../constants/urls';
import { STORAGE_KEYS } from '../constants';
import emptySplitApi from '../utils/rtk';

type CommonResponse = {
  statusCode: number;
  message?: string;
  data?: any;
};

function designListTags(res: any) {
  const data = res?.data || res;
  const items = Array.isArray(data)
    ? data
    : Array.isArray(data?.designs)
      ? data.designs
      : Array.isArray(data?.results)
        ? data.results
        : [];
  return [
    { type: 'DESIGNS' as const, id: 'LIST' },
    ...items
      .map((item: any) => item?.design_id)
      .filter(Boolean)
      .map((id: string) => ({ type: 'DESIGNS' as const, id })),
  ];
}

export const authApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<any, any>({
      query: (body) => ({
        url: END_POINTS.login,
        method: 'POST',
        body,
      }),
    }),
    signup: builder.mutation<any, any>({
      query: (body) => ({
        url: END_POINTS.createAccount,
        method: 'POST',
        body,
      }),
    }),
    verifyOtp: builder.mutation<any, any>({
      query: (body) => ({
        url: END_POINTS.verifyOtp,
        method: 'POST',
        body,
      }),
    }),
    getProfile: builder.query<any, void>({
      query: () => {
        let token = localStorage.getItem(STORAGE_KEYS.token) || "";
        token = token.replace(/['"]+/g, '');
        const headers: any = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        return {
          url: END_POINTS.profile,
          method: 'GET',
          headers
        };
      },
      providesTags: ['USER'],
      keepUnusedDataFor: 30,
    }),
    updateProfile: builder.mutation<any, any>({
      query: (body) => {
        let token = localStorage.getItem(STORAGE_KEYS.token) || "";
        token = token.replace(/['"]+/g, '');
        const headers: any = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        return {
          url: END_POINTS.updateProfile,
          method: 'PUT',
          body,
          headers
        };
      },
      invalidatesTags: ['USER'],
    }),
    logout: builder.mutation<any, any>({
      query: () => {
        let token = localStorage.getItem(STORAGE_KEYS.token) || "";
        token = token.replace(/['"]+/g, ''); // Strip accidental quotes just in case
        const headers: any = {
          'accept': '*/*'
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        return {
          url: END_POINTS.logout,
          method: 'GET',
          headers
        };
      },
    }),
    changePassword: builder.mutation<any, any>({
      query: (body) => {
        let token = localStorage.getItem(STORAGE_KEYS.token) || "";
        token = token.replace(/['"]+/g, '');
        const headers: any = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        return {
          url: END_POINTS.changePassword,
          method: 'POST',
          body,
          headers
        };
      },
    }),
    confirmPassword: builder.mutation<any, any>({
      query: (body) => ({
        url: END_POINTS.resetConfirm,
        method: 'POST',
        body,
      }),
    }),
    resetPassword: builder.mutation<any, any>({
      query: (body) => ({
        url: END_POINTS.resetPassword,
        method: 'POST',
        body,
      }),
    }),
    resendOtp: builder.mutation<any, any>({
      query: (body) => ({
        url: END_POINTS.resendOtp,
        method: 'POST',
        body,
      }),
    }),
    uploadMedia: builder.mutation<any, FormData>({
      query: (body) => ({
        url: END_POINTS.uploadMedia,
        method: 'POST',
        body,
      }),
    }),
    generateDesign: builder.mutation<any, any>({
      query: (body) => {
        let token = localStorage.getItem(STORAGE_KEYS.token) || "";
        token = token.replace(/['"]+/g, '');
        const headers: any = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        return {
          url: END_POINTS.generateDesign,
          method: 'POST',
          body,
          headers
        };
      },
      invalidatesTags: [{ type: 'DESIGNS', id: 'LIST' }],
    }),
    getDesigns: builder.query<any, { limit?: number; offset?: number; status?: string; search?: string; sort_by?: string } | void>({
      query: (arg) => {
        const params = new URLSearchParams();
        const limit = arg?.limit ?? 12;
        const offset = arg?.offset ?? 0;
        params.set('limit', String(limit));
        params.set('offset', String(offset));
        if (arg?.status) params.set('status', arg.status);
        if (arg?.search) params.set('search', arg.search);
        if (arg?.sort_by) params.set('sort_by', arg.sort_by);
        return {
          url: `${END_POINTS.designs}?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: (result) => designListTags(result),
    }),
    getDesign: builder.query<any, string>({
      query: (designId) => ({
        url: `${END_POINTS.designs}${encodeURIComponent(designId)}/`,
        method: 'GET',
      }),
      providesTags: (result, error, designId) => (error ? [] : [{ type: 'DESIGNS', id: designId }]),
    }),
    updateDesign: builder.mutation<any, { designId: string; body: any }>({
      query: ({ designId, body }) => ({
        url: `${END_POINTS.designs}${encodeURIComponent(designId)}/`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_res, _err, { designId }) => [{ type: 'DESIGNS', id: 'LIST' }, { type: 'DESIGNS', id: designId }],
    }),
    deleteDesign: builder.mutation<any, string>({
      query: (designId) => {
        let token = localStorage.getItem(STORAGE_KEYS.token) || "";
        token = token.replace(/['"]+/g, '');
        const headers: any = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        return {
          url: `${END_POINTS.deleteDesign}${encodeURIComponent(designId)}/`,
          method: 'DELETE',
          headers
        };
      },
      invalidatesTags: () => [{ type: 'DESIGNS', id: 'LIST' }],
    }),
    exportDesign: builder.mutation<any, { designId: string, format: string, resolution?: any }>({
      query: ({ designId, format, resolution }) => ({
        url: `/api/designs/${designId}/export/`,
        method: 'POST',
        body: {
          format,
          resolution: resolution || {},
        },
      }),
    }),
    getDesignExport: builder.query<any, { designId: string; exportId?: string }>({
      query: ({ designId, exportId }) => ({
        url: exportId
          ? `/api/designs/${designId}/export/${exportId}/`
          : `/api/designs/${designId}/export/`,
        method: 'GET',
      }),
    }),
    saveDesignFinal: builder.mutation<any, string>({
      query: (designId) => {
        let token = localStorage.getItem(STORAGE_KEYS.token) || "";
        token = token.replace(/['"]+/g, '');
        const headers: any = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        return {
          url: `/api/designs/${designId}/save/`,
          method: 'POST',
          headers
        };
      },
      invalidatesTags: (_res, _err, designId) => [{ type: 'DESIGNS', id: 'LIST' }, { type: 'DESIGNS', id: designId }],
    }),
    claimDesign: builder.mutation<any, string>({
      query: (designId) => {
        let token = localStorage.getItem(STORAGE_KEYS.token) || "";
        token = token.replace(/['"]+/g, '');
        const headers: any = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        return {
          url: `${END_POINTS.claimDesign}${encodeURIComponent(designId)}/claim/`,
          method: 'POST',
          headers
        };
      },
      invalidatesTags: (_res, _err, designId) => [{ type: 'DESIGNS', id: 'LIST' }, { type: 'DESIGNS', id: designId }],
    }),
    generateCertificate: builder.mutation<any, { design_id: string; use_s3?: boolean }>({
      query: (body) => {
        let token = localStorage.getItem(STORAGE_KEYS.token) || "";
        token = token.replace(/['"]+/g, '');
        const headers: any = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        return {
          url: END_POINTS.certificate,
          method: 'POST',
          body,
          headers
        };
      },
    }),
    getCms: builder.query<any, void>({
      query: () => END_POINTS.cms,
    }),
    getFaqs: builder.query<any, { search?: string } | void>({
      query: (arg) => {
        let url = END_POINTS.faqs;
        if (arg && arg.search) {
          url += `?search=${encodeURIComponent(arg.search)}`;
        }
        return url;
      },
    }),
  }),
});

export const {
  useLoginMutation,
  useSignupMutation,
  useVerifyOtpMutation,
  useChangePasswordMutation,
  useLogoutMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
  useResetPasswordMutation,
  useConfirmPasswordMutation,
  useResendOtpMutation,
  useGetCmsQuery,
  useGetFaqsQuery,
  useUploadMediaMutation,
  useGenerateDesignMutation,
  useGetDesignsQuery,
  useLazyGetDesignQuery,
  useUpdateDesignMutation,
  useDeleteDesignMutation,
  useExportDesignMutation,
  useLazyGetDesignExportQuery,
  useSaveDesignFinalMutation,
  useClaimDesignMutation,
  useGenerateCertificateMutation,
} = authApi;
